import { Button, Heading, Link as CLink, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router'
import { api } from '~/api/client'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { MemberLayout } from '~/components/member/layout'
import { Rich, useT } from '~/i18n/use-t'

/**
 * `/unsubscribe/:token` (R78): shows the masked address and one button that POSTs
 * `/api/public/unsubscribe/:token`. Opening the page changes nothing (R23: mail programs open links by themselves).
 */
export default function Unsubscribe() {
  const { t, config } = useT()
  const friendly = useFriendlyError()
  const { token = '' } = useParams()
  const info = useQuery({
    queryKey: ['public', 'unsubscribe', token],
    queryFn: () => api('unsubscribeInfo', { params: { token } }),
    retry: false,
  })
  const stop = useMutation({ mutationFn: () => api('unsubscribe', { params: { token } }) })

  return (
    <MemberLayout>
      <Stack gap={5}>
        <Heading as='h1' size={{ base: '2xl', md: '3xl' }}>
          {t('unsubscribe.title')}
        </Heading>
        {info.isPending && <Text role='status'>{t('common.loading')}</Text>}
        {/* An old link is not a dead end: the contact address is a link, and the start page is one tap away. */}
        {info.isError && (
          <>
            <Text role='alert'>
              <Rich
                k='unsubscribe.badLink'
                components={{
                  a: (
                    // Inline (it wraps with the sentence); the vertical padding makes it a 48 px tall target.
                    <CLink
                      href={`mailto:${config.org.contact}`}
                      display='inline'
                      textDecoration='underline'
                      overflowWrap='anywhere'
                      py={3.5}
                    />
                  ),
                }}
              />
            </Text>
            <ActionBar>
              <Button asChild size='lg' variant='outline' w='full'>
                <RouterLink to='/join'>{t('unsubscribe.toStart')}</RouterLink>
              </Button>
            </ActionBar>
          </>
        )}
        {info.data &&
          (stop.isSuccess ? (
            <Text role='status'>{t('unsubscribe.done')}</Text>
          ) : info.data.unsubscribed ? (
            <Text>
              <Rich k='unsubscribe.already' values={{ email: info.data.email }} />
            </Text>
          ) : (
            <>
              <Text>
                <Rich k='unsubscribe.text' values={{ email: info.data.email }} />
              </Text>
              {stop.isError && <ErrorAlert>{friendly(stop.error)}</ErrorAlert>}
              <ActionBar>
                <Button size='lg' w='full' loading={stop.isPending} onClick={() => stop.mutate()}>
                  {t('unsubscribe.button')}
                </Button>
              </ActionBar>
            </>
          ))}
      </Stack>
    </MemberLayout>
  )
}
